<template>
  <div class="viewer-root" role="dialog" aria-modal="true" :aria-label="title" @keydown="onKeydown">
    <div class="viewer-backdrop" @click="$emit('close')"></div>

    <div ref="panel" class="viewer-panel" tabindex="-1">
      <header class="viewer-head">
        <div class="viewer-heading">
          <div class="viewer-title">{{ title }}</div>
          <div v-if="files.length > 1" class="viewer-sub">{{ file.label }} · {{ index + 1 }} de {{ files.length }}</div>
        </div>
        <div class="viewer-actions">
          <div v-if="files.length > 1" class="viewer-switch" role="group" aria-label="Archivo">
            <button
              v-for="(f, i) in files"
              :key="f.prefix"
              type="button"
              class="switch-btn"
              :class="{ active: i === index }"
              :aria-pressed="i === index ? 'true' : 'false'"
              @click="index = i"
            >
              {{ f.label }}
            </button>
          </div>
          <button
            v-if="status === 'ready'"
            type="button"
            class="icon-btn"
            :aria-label="zoomed ? 'Ajustar a la pantalla' : 'Ampliar'"
            :title="zoomed ? 'Ajustar a la pantalla' : 'Ampliar'"
            @click="toggleZoom"
          >
            <PortalIcon :name="zoomed ? 'zoom-out' : 'zoom-in'" :size="18" />
          </button>
          <button type="button" class="icon-btn" aria-label="Cerrar" title="Cerrar" @click="$emit('close')">
            <PortalIcon name="x" :size="18" />
          </button>
        </div>
      </header>

      <!-- View only: no download button, and no context menu (so no "save image as" either). -->
      <div class="viewer-stage" :class="{ zoomed }" @contextmenu.prevent>
        <div v-if="status === 'loading'" class="viewer-state">
          <span class="viewer-spinner" aria-hidden="true"></span>
          <span>Cargando documento…</span>
        </div>
        <div v-else-if="status === 'error'" class="viewer-state">
          <span class="state-icon"><PortalIcon name="alert" :size="22" /></span>
          <div class="state-title">No se pudo mostrar el documento</div>
          <p class="state-text">Revise su conexión e intente de nuevo.</p>
          <button type="button" class="retry-btn" @click="load()">Reintentar</button>
        </div>

        <img
          v-if="url"
          v-show="status === 'ready'"
          :key="url"
          :src="url"
          :alt="files.length > 1 ? `${title} — ${file.label}` : title"
          class="viewer-img"
          draggable="false"
          @load="status = 'ready'"
          @error="status = 'error'"
          @click="toggleZoom"
        />
      </div>
    </div>
  </div>
</template>

<script>
import * as candidateApi from "../../services/candidateApi";
import PortalIcon from "./PortalIcon.vue";

// In-page viewer for the applicant's stored images (FileLink downloads anything else).
// Stored files have no content type, so opening their link would download them; an
// <img> shows them regardless.
export default {
  name: "DocumentViewer",
  components: { PortalIcon },
  props: {
    title: { type: String, required: true },
    // [{ label, prefix }] — the document's images (e.g. both sides of the cédula).
    files: { type: Array, required: true },
    initialIndex: { type: Number, default: 0 },
  },
  data() {
    return {
      index: this.initialIndex,
      // loading | ready | error
      status: "loading",
      url: null,
      zoomed: false,
      loadId: 0,
      previousBodyOverflow: "",
    };
  },
  computed: {
    file() {
      return this.files[this.index];
    },
  },
  watch: {
    index() {
      this.load();
    },
  },
  mounted() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.$refs.panel.focus();
    this.load();
  },
  beforeDestroy() {
    document.body.style.overflow = this.previousBodyOverflow;
  },
  methods: {
    // The <img> marks it ready once loaded (see @load).
    load() {
      const loadId = ++this.loadId;
      this.status = "loading";
      this.url = null;
      this.zoomed = false;
      candidateApi
        .getFileUrl(this.file.prefix)
        .then((response) => {
          if (loadId !== this.loadId) return;
          if (!response.data) throw new Error("file url missing");
          this.url = response.data;
        })
        .catch(() => {
          if (loadId === this.loadId) this.status = "error";
        });
    },
    toggleZoom() {
      if (this.status === "ready") this.zoomed = !this.zoomed;
    },
    onKeydown(e) {
      if (e.key === "Escape") {
        this.$emit("close");
      } else if (this.files.length > 1 && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
        const step = e.key === "ArrowRight" ? 1 : -1;
        this.index = (this.index + step + this.files.length) % this.files.length;
      }
    },
  },
};
</script>

<style scoped>
.viewer-root {
  position: fixed;
  inset: 0;
  /* Above the portal's side panel (900) and toast (950). */
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.viewer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 16, 32, 0.82);
}
.viewer-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(1040px, 100%);
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.6);
  outline: none;
  -webkit-font-smoothing: antialiased;
}
.viewer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px 12px 20px;
  border-bottom: 1px solid #eceef3;
}
.viewer-heading {
  min-width: 0;
}
.viewer-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 600 16px/1.3 "Geist", system-ui, sans-serif;
  letter-spacing: -0.01em;
  color: #0e1424;
}
.viewer-sub {
  margin-top: 1px;
  font: 400 13px "Geist", system-ui, sans-serif;
  color: #8a91a3;
}
.viewer-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.viewer-switch {
  display: flex;
  gap: 3px;
  padding: 3px;
  border-radius: 10px;
  background: #f4f5f8;
  box-shadow: inset 0 0 0 1px #e6e8ee;
}
.switch-btn {
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #6b7283;
  cursor: pointer;
}
.switch-btn.active {
  background: #fff;
  color: #0e1424;
  box-shadow: 0 1px 3px rgba(10, 16, 32, 0.14);
}
.icon-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #fff;
  color: #6b7283;
  cursor: pointer;
}
.icon-btn:hover {
  background: #f4f5f8;
  color: #0e1424;
}

.viewer-stage {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: auto;
  padding: 24px;
  background: #eef0f4;
  user-select: none;
  -webkit-user-select: none;
}
/* margin: auto (not justify/align center) keeps an enlarged image scrollable from its edges. */
.viewer-img {
  display: block;
  margin: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background: #fff;
  box-shadow: 0 10px 30px -12px rgba(10, 16, 32, 0.45);
  cursor: zoom-in;
  -webkit-user-drag: none;
  -webkit-touch-callout: none;
}
.zoomed .viewer-img {
  width: 200%;
  max-width: none;
  max-height: none;
  cursor: zoom-out;
}

.viewer-state {
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 360px;
  text-align: center;
  font: 400 14px "Geist", system-ui, sans-serif;
  color: #5d6578;
}
.viewer-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #dfe2ea;
  border-top-color: #b7832a;
  border-radius: 50%;
  animation: viewer-spin 0.8s linear infinite;
}
@keyframes viewer-spin {
  to {
    transform: rotate(360deg);
  }
}
.state-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdecec;
  box-shadow: inset 0 0 0 1px #f6c9c9;
  color: #c42b2b;
}
.state-title {
  font: 600 15px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.state-text {
  margin: 0;
  font: 400 14px/1.5 "Geist", system-ui, sans-serif;
}
.retry-btn {
  margin-top: 6px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: #0e1424;
  box-shadow: 0 4px 12px -4px rgba(10, 16, 32, 0.5);
  color: #fff;
  font: 600 13px "Geist", system-ui, sans-serif;
  cursor: pointer;
}
.retry-btn:hover {
  background: #24304d;
}

@media (max-width: 640px) {
  .viewer-root {
    padding: 0;
  }
  .viewer-panel {
    border-radius: 0;
  }
  .viewer-head {
    padding: 10px 10px 10px 14px;
  }
  .viewer-stage {
    padding: 14px;
  }
  .switch-btn {
    padding: 0 9px;
  }
}
</style>
